import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

export type PmSavedViewScope = 'tasks' | 'projects' | 'reports';
export type PmSavedViewVisibility = 'personal' | 'org';

export interface PmSavedView {
  id: string;
  organization_id: string;
  user_id: string;
  scope: PmSavedViewScope;
  name: string;
  visibility: PmSavedViewVisibility;
  payload: Record<string, any>;
  created_at: string;
  updated_at: string;
}

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

const API_BASE_URL = ENV.API_URL;

export const pmSavedViewsService = {
  async list(params: { organization_id: string; scope: PmSavedViewScope }): Promise<{ data: PmSavedView[]; error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({
        organization_id: params.organization_id,
        scope: params.scope,
      });

      const response = await fetch(`${API_BASE_URL}/api/project-management/saved-views?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: [], error: result?.error || 'Failed to fetch saved views' };
      }

      return { data: (result.data || []) as PmSavedView[], error: null };
    } catch (e: any) {
      return { data: [], error: e?.message || 'Network error' };
    }
  },

  async upsert(input: {
    organization_id: string;
    scope: PmSavedViewScope;
    name: string;
    visibility: PmSavedViewVisibility;
    payload: Record<string, any>;
  }): Promise<{ data: PmSavedView | null; error: any }> {
    try {
      const token = await getToken();

      const response = await fetch(`${API_BASE_URL}/api/project-management/saved-views`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: result?.error || 'Failed to save view' };
      }

      return { data: result.data as PmSavedView, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || 'Network error' };
    }
  },

  async delete(id: string, organizationId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const queryParams = new URLSearchParams({ organization_id: organizationId });

      const response = await fetch(
        `${API_BASE_URL}/api/project-management/saved-views/${encodeURIComponent(id)}?${queryParams.toString()}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();
      if (!response.ok) {
        return { error: result?.error || 'Failed to delete view' };
      }

      return { error: null };
    } catch (e: any) {
      return { error: e?.message || 'Network error' };
    }
  },
};
